/**
 * Small IK toolkit for the full-body avatars: an analytic two-bone solver (arm, leg) and a
 * bone aimer that works for any rig by comparing against the recorded rest pose, so Mixamo's
 * bone-axis conventions never need to be known.
 */
import * as THREE from 'three';

const _a = new THREE.Vector3();
const _b = new THREE.Vector3();
const _n = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _pq = new THREE.Quaternion();

/**
 * Analytic two-bone IK. Given the root joint position, a target for the end effector, the two
 * segment lengths and a pole direction (which way the middle joint should bend), returns the
 * middle joint position. The chain straightens (never over-reaches) when the target is too far.
 */
export function solveTwoBone(
  root: THREE.Vector3,
  target: THREE.Vector3,
  pole: THREE.Vector3,
  lengthA: number,
  lengthB: number,
  out = new THREE.Vector3(),
): THREE.Vector3 {
  const maxReach = (lengthA + lengthB) * 0.999;
  _a.subVectors(target, root);
  let d = _a.length();
  if (d < 1e-5) {
    return out.copy(root).addScaledVector(pole, lengthA);
  }
  if (d > maxReach) d = maxReach;
  _a.normalize();
  // Component of the pole perpendicular to the root->target axis.
  _n.copy(pole).addScaledVector(_a, -pole.dot(_a));
  if (_n.lengthSq() < 1e-6) {
    // Pole parallel to the chain: pick any perpendicular.
    _n.set(_a.y, -_a.x, 0);
    if (_n.lengthSq() < 1e-6) _n.set(0, -_a.z, _a.y);
  }
  _n.normalize();
  // Law of cosines: distance of the middle joint along the axis and its offset.
  const along = THREE.MathUtils.clamp(
    (lengthA * lengthA - lengthB * lengthB + d * d) / (2 * d),
    -lengthA,
    lengthA,
  );
  const offset = Math.sqrt(Math.max(0, lengthA * lengthA - along * along));
  return out.copy(root).addScaledVector(_a, along).addScaledVector(_n, offset);
}

/** Rest-pose record for a bone: where it pointed and how it was oriented in world space. */
export interface BoneRest {
  bone: THREE.Bone;
  restWorldQuat: THREE.Quaternion;
  /** Unit vector from this bone to its child (or along its local axis) in world rest space. */
  restDir: THREE.Vector3;
  length: number;
}

export function recordRest(bone: THREE.Bone, child: THREE.Object3D | undefined): BoneRest {
  bone.updateWorldMatrix(true, false);
  const restWorldQuat = new THREE.Quaternion();
  bone.getWorldQuaternion(restWorldQuat);
  const from = bone.getWorldPosition(new THREE.Vector3());
  const restDir = new THREE.Vector3();
  let length = 0.1;
  if (child) {
    child.updateWorldMatrix(true, false);
    const to = child.getWorldPosition(new THREE.Vector3());
    restDir.subVectors(to, from);
    length = restDir.length();
    restDir.normalize();
  } else {
    restDir.set(0, 1, 0).applyQuaternion(restWorldQuat);
  }
  return { bone, restWorldQuat, restDir, length };
}

/**
 * Points a bone so its rest direction lines up with `direction` (world space) while keeping
 * its rest roll. Uses the parent's current world orientation, so call in hierarchy order.
 *
 * `frame` is how the character has turned since the rest pose was recorded (in practice a
 * yaw). Rest orientation and direction are carried through it first, so the bone keeps its
 * rest roll relative to the body rather than to the world: without it a chest whose aim is
 * straight up keeps facing the rest direction whichever way the body turns, and a foot aimed
 * opposite to its rest direction flips over.
 */
export function aimBone(rest: BoneRest, direction: THREE.Vector3, frame?: THREE.Quaternion): void {
  _b.copy(direction).normalize();
  if (frame) {
    _a.copy(rest.restDir).applyQuaternion(frame);
    _q.setFromUnitVectors(_a, _b).multiply(frame).multiply(rest.restWorldQuat);
  } else {
    _q.setFromUnitVectors(rest.restDir, _b).multiply(rest.restWorldQuat);
  }
  setWorldQuaternion(rest.bone, _q);
}

/** Sets a bone's world-space orientation to `world` (parent must be up to date). */
export function setWorldQuaternion(bone: THREE.Object3D, world: THREE.Quaternion): void {
  const parent = bone.parent;
  if (parent) {
    parent.getWorldQuaternion(_pq);
    bone.quaternion.copy(_pq.invert().multiply(world));
  } else {
    bone.quaternion.copy(world);
  }
  bone.updateWorldMatrix(false, false);
}

/**
 * Orients a bone so that its rest "forward" and "up" axes map onto the given world axes.
 * Used for hands: forward = along the fingers, up = back of the hand.
 */
export function orientBone(
  rest: BoneRest,
  restUp: THREE.Vector3,
  forward: THREE.Vector3,
  up: THREE.Vector3,
): void {
  const m = new THREE.Matrix4();
  const f = forward.clone().normalize();
  const u = up.clone().normalize();
  const r = new THREE.Vector3().crossVectors(u, f).normalize();
  u.crossVectors(f, r).normalize();
  m.makeBasis(r, u, f);
  const target = new THREE.Quaternion().setFromRotationMatrix(m);
  // Same basis in rest space.
  const rf = rest.restDir.clone().normalize();
  const ru = restUp.clone().normalize();
  const rr = new THREE.Vector3().crossVectors(ru, rf).normalize();
  ru.crossVectors(rf, rr).normalize();
  const restBasis = new THREE.Quaternion().setFromRotationMatrix(
    new THREE.Matrix4().makeBasis(rr, ru, rf),
  );
  // world = target * inverse(restBasis) * restWorldQuat
  _q.copy(target).multiply(restBasis.invert()).multiply(rest.restWorldQuat);
  setWorldQuaternion(rest.bone, _q);
}
